import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { isUserAdmin } from "../utils/userUtils";

const formatDate = (raw) => {
  if (!raw) return "–";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const MatchResultInput = ({ match, onSave }) => {
  const { user } = useAuth();
  const isScheduled = !!(match.startTime || match.date);
  const resultLocked = !isUserAdmin(user) && match.resultEnteredAt
    && (Date.now() - new Date(match.resultEnteredAt).getTime() > 24 * 60 * 60 * 1000);
  const initialSets = [1, 2, 3].map((nr) => {
    const set = match.sets?.find((s) => s.setNumber === nr);
    return [set?.player1Score ?? "", set?.player2Score ?? ""];
  });
  const [sets, setSets] = useState(initialSets);
  const [dirty, setDirty] = useState(false);
  const [date, setDate] = useState(match.date || "");
  const [court, setCourt] = useState(match.court || "");

  // Refs auf die Satz-Eingabefelder für Auto-Advance beim Eintippen.
  const inputRefs = useRef([]);

  const setInputRef = (setIdx, fieldIdx) => (el) => {
    if (!inputRefs.current[setIdx]) inputRefs.current[setIdx] = [];
    inputRefs.current[setIdx][fieldIdx] = el;
  };

  // Nach Eingabe in den ersten beiden Sätzen automatisch ins nächste Feld
  // springen. Im dritten Satz nicht, da dort (Match-Tiebreak) zweistellige
  // Werte vorkommen können.
  const focusNextField = (setIdx, fieldIdx) => {
    if (setIdx > 1) return;
    let nextSet = setIdx;
    let nextField = fieldIdx + 1;
    if (nextField > 1) {
      nextField = 0;
      nextSet = setIdx + 1;
    }
    const nextInput = inputRefs.current?.[nextSet]?.[nextField];
    if (nextInput) {
      nextInput.focus();
      if (typeof nextInput.select === "function") nextInput.select();
    }
  };

  useEffect(() => {
    setSets(
      [1, 2, 3].map((nr) => {
        const set = match.sets?.find((s) => s.setNumber === nr);
        return [set?.player1Score ?? "", set?.player2Score ?? ""];
      })
    );
    setDate(match.date || "");
    setCourt(match.court || "");
    setDirty(false);
  }, [match.sets, match.id, match.date, match.court]);

  const handleSetChange = (setIdx, fieldIdx, value) => {
    const newSets = sets.map((arr, idx) =>
      idx === setIdx
        ? [fieldIdx === 0 ? value : arr[0], fieldIdx === 1 ? value : arr[1]]
        : arr
    );
    setSets(newSets);
    setDirty(true);

    // Nach Eingabe einer Ziffer automatisch ins nächste Feld springen.
    if (value !== "") {
      focusNextField(setIdx, fieldIdx);
    }
  };

  const handleSave = () => {
    const setResults = sets
      .map(([p1, p2], idx) =>
        p1 !== "" && p2 !== ""
          ? {
              setNumber: idx + 1,
              player1Score: Number(p1),
              player2Score: Number(p2),
            }
          : null
      )
      .filter(Boolean);
    onSave(match.id, setResults, date, court);
    setDirty(false);
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSave();
      }}
      style={{ display: "flex", flexDirection: "column", gap: 12 }}
    >
      <div className="mri-meta-row">
        <label className="mri-field">
          Datum:
          {isScheduled ? (
            <span>{formatDate(date)}</span>
          ) : (
            <input
              type="date"
              value={date || ""}
              onChange={(e) => {
                setDate(e.target.value);
                setDirty(true);
              }}
              className="groupphase-input"
            />
          )}
        </label>
        <label className="mri-field">
          Platz:
          {isScheduled ? (
            <span>{court || "–"}</span>
          ) : (
            <input
              type="text"
              value={court || ""}
              onChange={(e) => {
                setCourt(e.target.value);
                setDirty(true);
              }}
              className="groupphase-input"
            />
          )}
        </label>
      </div>
      {resultLocked && (
        <div style={{ color: "#888", fontSize: "0.85rem", fontStyle: "italic" }}>
          Das Ergebnis wurde vor mehr als 24 Stunden eingetragen und kann nur noch von Admins geändert werden.
        </div>
      )}
      <div className="mri-sets-row">
        {[0, 1, 2].map((setIdx) => (
          <span key={setIdx} className="mri-set">
            <label>Satz {setIdx + 1}</label>
            <input
              ref={setInputRef(setIdx, 0)}
              type="number"
              min="0"
              value={sets[setIdx][0]}
              placeholder="0"
              onChange={(e) => handleSetChange(setIdx, 0, e.target.value)}
              className="groupphase-input"
              style={{ width: 38, textAlign: "center" }}
              disabled={resultLocked}
            />
            <span>:</span>
            <input
              ref={setInputRef(setIdx, 1)}
              type="number"
              min="0"
              value={sets[setIdx][1]}
              placeholder="0"
              onChange={(e) => handleSetChange(setIdx, 1, e.target.value)}
              className="groupphase-input"
              style={{ width: 38, textAlign: "center" }}
              disabled={resultLocked}
            />
          </span>
        ))}
      </div>
      <button
        type="submit"
        disabled={!dirty || resultLocked}
        className="groupphase-button mri-save-btn"
      >
        Speichern
      </button>
    </form>
  );
};

export default MatchResultInput;
